import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PictureOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import {
  CommentDomainService,
  CommentEntity,
  CommentItem,
  decodeHtmlEntities,
  useFetchComments
} from 'domain/comment'
import { AnnotationClickHandler, SceneNavigationHandler } from 'domain/krpano'
import useAuth from 'hooks/useAuth'
import useBuildingUsersAndGroups from 'hooks/useBuildingUsersAndGroups'
import useDirectory from 'hooks/useDirectory'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
  buildingModeState,
  isRefetchingAnnotationsState,
  listPointState,
  revalidatePointDetailState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import { fetchCommentBodyContent, isS3Url } from 'utils/commentS3Utils'
import { KrpanoEngine } from 'utils/KrpanoEngine'
import { KrpanoRN } from 'utils/krpanoRN'

type Props = {
  handleCollapse?: () => void
}

function ResolvedCommentBodyPreview({ item }: { item: CommentItem }) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  )
  const [content, setContent] = useState('')

  useEffect(() => {
    let isMounted = true
    const targetPath = (item as any)?.s3_path || item.s3Path || item.body || ''
    if (!targetPath) {
      setStatus('success')
      setContent('')
      return
    }
    setStatus('loading')
    fetchCommentBodyContent(targetPath)
      .then((text) => {
        if (isMounted) {
          if (isS3Url(text)) {
            setStatus('error')
          } else {
            setContent(text || '')
            setStatus('success')
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error')
        }
      })
    return () => {
      isMounted = false
    }
  }, [item])

  if (status === 'loading') {
    return (
      <div className="mt-0.5 text-[11px] text-slate-400 italic">
        [ 読み込み中... ]
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="mt-0.5 text-[11px] text-rose-500 font-semibold">
        [ 取得失敗 ]
      </div>
    )
  }

  const hasImage = /<img[\s\S]*?>/i.test(content)

  const textOnly = decodeHtmlEntities(
    content
      .replace(/<img[\s\S]*?>/gi, ' ')
      .replace(/<\/(p|div|h[1-6]|li|tr|br\s*\/?)>/gi, ' ')
      .replace(/<[^>]*>?/g, '')
  )
    .replace(/\s+/g, ' ')
    .trim()

  if (!textOnly && !hasImage) return null

  return (
    <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-slate-500 leading-relaxed">
      {hasImage && (
        <span
          className="inline-flex items-center text-slate-400 flex-none text-[12px]"
          title="画像あり"
        >
          <PictureOutlined />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{textOnly || '[画像]'}</span>
    </div>
  )
}

export default function CommentTab({ handleCollapse = () => {} }: Props) {
  const { id: buildingId } = useParams()
  const { userList } = useBuildingUsersAndGroups(buildingId)
  const { profile } = useAuth()
  const { getDirectory, getBuiildingType } = useDirectory()
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const points = useRecoilValue(listPointState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const selectedPointValue = useRecoilValue(selectedPoint)
  const currentViewer = useRecoilValue(currentViewerState)
  const setSelectedPointState = useSetRecoilState(selectedPoint)
  const setSelectedSubImageState = useSetRecoilState(selectedSubImageState)
  const setBuildingMode = useSetRecoilState(buildingModeState)
  const isRefetchingAnnotations = useRecoilValue(isRefetchingAnnotationsState)
  const setIsRefetchingAnnotations = useSetRecoilState(
    isRefetchingAnnotationsState
  )
  const setRevalidatePointDetail = useSetRecoilState(revalidatePointDetailState)

  const activePointId = useMemo(() => {
    return (
      (currentViewer?.selectedPoint
        ? String(currentViewer.selectedPoint)
        : undefined) ||
      (selectedPointValue ? String(selectedPointValue) : undefined) ||
      (pointDetail?.id ? String(pointDetail.id) : undefined) ||
      ''
    )
  }, [currentViewer?.selectedPoint, selectedPointValue, pointDetail?.id])

  const activePointObj = useMemo(() => {
    if (pointDetail && String(pointDetail.id) === activePointId) {
      return pointDetail
    }
    if (points && Array.isArray(points)) {
      return points.find((pt: any) => String(pt.id) === activePointId)
    }
    return pointDetail
  }, [pointDetail, points, activePointId])

  const { comments, unfilteredTotal, isLoading, mutate } = useFetchComments({
    buildingId: buildingId!,
    pointId: activePointId,
    status: statusFilter
  })

  const [isRefreshing, setIsRefreshing] = useState(false)
  const isWaitingForSyncRef = useRef(false)
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleRefresh = async () => {
    if (isRefreshing || isLoading || isRefetchingAnnotations) return
    setIsRefreshing(true)
    isWaitingForSyncRef.current = true

    if (activePointId) {
      CommentDomainService.triggerAnnotationRefresh(
        activePointId,
        setIsRefetchingAnnotations,
        setRevalidatePointDetail
      )
    } else {
      await mutate?.().finally(() => {
        setIsRefreshing(false)
      })
      isWaitingForSyncRef.current = false
      return
    }

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    syncTimeoutRef.current = setTimeout(() => {
      if (isWaitingForSyncRef.current) {
        isWaitingForSyncRef.current = false
        mutate?.().finally(() => {
          setIsRefreshing(false)
        })
      }
    }, 6000)
  }

  useEffect(() => {
    if (isWaitingForSyncRef.current && !isRefetchingAnnotations) {
      isWaitingForSyncRef.current = false
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
      mutate?.().finally(() => {
        setIsRefreshing(false)
      })
    }
  }, [isRefetchingAnnotations, mutate])

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    }
  }, [])

  const [activeCategories, setActiveCategories] = useState<string[]>(
    KrpanoRN.categories
  )
  const [activeFeatures, setActiveFeatures] = useState<string[]>(
    KrpanoRN.features
  )

  useEffect(() => {
    const handleFilterChange = (e: Event) => {
      const { detail } = e as CustomEvent
      if (detail) {
        if (Array.isArray(detail.categories)) {
          setActiveCategories(detail.categories)
        }
        if (Array.isArray(detail.features)) {
          setActiveFeatures(detail.features)
        }
      }
    }

    window.addEventListener('rn_marker_filter_change', handleFilterChange)
    return () => {
      window.removeEventListener('rn_marker_filter_change', handleFilterChange)
    }
  }, [])

  const activeAnnotationIds = useMemo(() => {
    const ids = new Set<string>()
    if (points && Array.isArray(points)) {
      points.forEach((pt: any) => {
        if (pt?.annotations && Array.isArray(pt.annotations)) {
          pt.annotations.forEach((anno: any) => {
            if (anno?.id) ids.add(String(anno.id))
          })
        }
      })
    }
    if (pointDetail?.annotations && Array.isArray(pointDetail.annotations)) {
      pointDetail.annotations.forEach((anno: any) => {
        if (anno?.id) ids.add(String(anno.id))
      })
    }
    return ids
  }, [points, pointDetail])

  const filteredComments = useMemo(() => {
    const isCommentFeatureSelected = activeFeatures.includes('comment')
    if (!isCommentFeatureSelected) {
      return []
    }
    return comments.filter((c: CommentItem) => {
      const annoId = c.annotationId || (c as any).annotation_id
      if (
        annoId &&
        activeAnnotationIds.size > 0 &&
        !activeAnnotationIds.has(String(annoId))
      ) {
        return false
      }

      const cat = (c.objectCat || 'G').toUpperCase()
      const matchesCat = activeCategories.some(
        (selectedCat) => selectedCat.toUpperCase() === cat
      )
      if (!matchesCat) return false

      return true
    })
  }, [comments, activeCategories, activeFeatures, activeAnnotationIds])

  const navigateKrpanoToTarget = (
    targetH: number,
    targetV: number,
    pointId: string,
    subImgId?: number | string | null
  ) => {
    const moveKrpano = () => {
      const krpano =
        new KrpanoEngine('full').instance ||
        new KrpanoEngine('viewer').instance ||
        (window as any).krpano ||
        (document.getElementById('embedpano-full') as any)

      const screenWidth =
        typeof window !== 'undefined' ? window.innerWidth || 1024 : 1024
      const modalWidth = Math.max(screenWidth * 0.5, 670)
      const modalLeftEdge = Math.max(0, screenWidth - 60 - modalWidth)
      const visibleCenter = modalLeftEdge / 2
      const shiftRatio = (screenWidth / 2 - visibleCenter) / screenWidth

      let hfov = 90
      if (krpano && typeof krpano.get === 'function') {
        hfov = Number(krpano.get('view.hfov') || krpano.get('view.fov') || 90)
      }
      const shiftedH = targetH + hfov * shiftRatio

      SceneNavigationHandler.loadScene({
        krpano,
        pointId,
        points,
        pointDetail: activePointObj || pointDetail,
        subImageId: subImgId,
        targetH: shiftedH,
        targetV,
        targetFov: 100,
        isTourVersion: getBuiildingType() === 10,
        directory: getDirectory(),
        userList,
        currentUser: profile,
        smoothSpeed: 'smooth(720,360,360)'
      })
    }
    moveKrpano()
    setTimeout(moveKrpano, 100)
    setTimeout(moveKrpano, 350)
  }

  const handleCommentClick = async (commentItem: CommentItem) => {
    setBuildingMode((prev) => ({
      ...prev,
      previewMode: true
    }))

    const pId = String(
      commentItem.pointId || (commentItem as any).point_id || ''
    )
    const subImgId = commentItem.subImageId ?? (commentItem as any).sub_image_id

    if (pId) {
      setSelectedPointState(pId)
    }

    if (subImgId) {
      setSelectedSubImageState(Number(subImgId))
    } else {
      setSelectedSubImageState(undefined)
    }

    const { annotationId, h, v } =
      await CommentDomainService.resolveCommentTargetPosition(
        commentItem,
        points
      )

    if (h !== undefined && v !== undefined) {
      const targetH = Number(h)
      const targetV = Number(v)
      if (!Number.isNaN(targetH) && !Number.isNaN(targetV)) {
        navigateKrpanoToTarget(targetH, targetV, pId, subImgId)
      }
    }

    if (annotationId) {
      AnnotationClickHandler.clickAnnotation(annotationId)
    }
  }

  const renderListContent = () => {
    if (!activePointId) {
      return (
        <div className="text-center py-12 text-slate-400 text-xs font-semibold">
          ポイントを選択してください。
        </div>
      )
    }

    if (isLoading && filteredComments.length === 0) {
      return (
        <div className="text-center py-12 text-slate-400 text-xs">
          読み込み中...
        </div>
      )
    }

    if (filteredComments.length === 0) {
      return (
        <div className="text-center py-12 text-slate-400 text-xs font-semibold">
          条件に合うコメントはありません。
        </div>
      )
    }

    return filteredComments.map((item: CommentItem) => {
      const catColor = CommentDomainService.getCategoryColor(item.objectCat)
      const isOpen = item.status === 'open'
      const replyCount = (item.replies || []).length
      const recipientDisplay =
        CommentEntity.from(item).getRecipientDisplayInfo(userList)
      const formattedDate =
        item.createdAt && dayjs(item.createdAt).isValid()
          ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')
          : ''
      const authorName =
        item.creatorName ||
        (item.author
          ? `${item.author.lastName || ''} ${
              item.author.firstName || ''
            }`.trim()
          : 'ユーザー')
      return (
        <button
          key={item.id}
          type="button"
          onClick={() => handleCommentClick(item)}
          className="block w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors bg-transparent cursor-pointer border-x-0 border-t-0 border-b border-solid"
          style={{ borderBottomColor: 'rgb(226, 232, 240)' }}
        >
          <div className="flex items-start gap-2">
            <span
              className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-black text-white"
              style={{
                backgroundColor: catColor,
                fontFamily: 'Georgia, serif'
              }}
            >
              {item.objectCat || 'A'}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <div className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-slate-800">
                  {item.title}
                </div>
                <span
                  className={`flex-none rounded-full px-1.5 py-px text-[9px] font-bold ${
                    isOpen
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {isOpen ? '未解決' : '✓解決'}
                </span>
              </div>

              <ResolvedCommentBodyPreview item={item} />

              <div className="mt-1 flex items-center gap-2 whitespace-nowrap text-[10.5px] text-slate-500">
                <span className="flex flex-none items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-calendar"
                  >
                    <path d="M8 2v4" />
                    <path d="M16 2v4" />
                    <rect width="18" height="18" x="3" y="4" rx="2" />
                    <path d="M3 10h18" />
                  </svg>
                  {formattedDate}
                </span>

                <span className="h-3 w-px flex-none bg-slate-300" />

                <span className="min-w-0 truncate font-semibold text-slate-600 max-w-[90px]">
                  {authorName}
                </span>

                {replyCount > 0 && (
                  <>
                    <span className="h-3 w-px flex-none bg-slate-300" />
                    <span className="flex flex-none items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-reply"
                      >
                        <polyline points="9 17 4 12 9 7" />
                        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                      </svg>
                      {replyCount}
                    </span>
                  </>
                )}

                <span className="ml-auto flex-none">
                  <span
                    className="flex items-center gap-0.5 rounded-full px-1.5 ring-2 ring-white"
                    title={recipientDisplay.tooltipTitle}
                    style={{
                      backgroundColor: recipientDisplay.badgeColor,
                      height: '16px',
                      paddingTop: '1px',
                      paddingBottom: '1px'
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-users"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span className="text-[9px] font-bold text-white leading-none">
                      {recipientDisplay.badgeText}
                    </span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </button>
      )
    })
  }

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ backgroundColor: 'rgb(255, 249, 230)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2"
        style={{
          background:
            'linear-gradient(rgb(255, 255, 255) 0%, rgb(236, 254, 255) 100%)'
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-7 w-7 flex-none items-center justify-center rounded-md"
            style={{ backgroundColor: 'rgb(8, 145, 178)' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-message-square-more"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M8 10h.01" />
              <path d="M12 10h.01" />
              <path d="M16 10h.01" />
            </svg>
          </span>
          <h2 className="truncate whitespace-nowrap text-[14px] font-bold text-slate-800 mb-0">
            コメント
          </h2>
          <span className="flex-none rounded-full bg-cyan-100 px-1.5 py-px text-[10px] font-bold text-cyan-700">
            {filteredComments.length}/{unfilteredTotal || comments.length}
          </span>
        </div>
        <button
          type="button"
          className="flex-none rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 border-0 bg-transparent outline-none cursor-pointer"
          title="パネルを閉じる"
          onClick={handleCollapse}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Filter Bar with Status Filter on Far Left and Reload Button on Far Right */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-1.5 text-[10.5px]">
        <div className="flex min-w-0 items-center gap-1">
          <span className="flex-none whitespace-nowrap text-slate-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-filter inline"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>{' '}
            状態
          </span>
          <select
            className="min-w-0 rounded border border-slate-300 bg-white px-1 py-0.5 outline-none focus:border-cyan-500 text-[10px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">すべて</option>
            <option value="open">未解決</option>
            <option value="resolved">解決済</option>
          </select>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={isRefreshing || isLoading}
          className={clsx(
            'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11.5px] font-medium rounded-md border border-solid border-slate-300 bg-white text-slate-700 shadow-sm transition-all select-none',
            isRefreshing || isLoading
              ? 'opacity-60 cursor-not-allowed'
              : 'cursor-pointer hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400 active:bg-slate-100'
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={clsx(
              'lucide lucide-rotate-cw flex-none text-slate-500',
              (isRefreshing || isLoading) && 'animate-spin text-sky-600'
            )}
          >
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
          <span>再読み込み</span>
        </button>
      </div>

      {/* Comment List Body with Loading Overlay */}
      <div className="relative flex-1 overflow-y-auto">
        {(isRefreshing || (isLoading && filteredComments.length > 0)) && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/60 backdrop-blur-[1px] select-none transition-all">
            <Spin size="default" />
            <span className="text-[12px] font-medium text-slate-500">
              再読み込み中...
            </span>
          </div>
        )}
        {renderListContent()}
      </div>
    </div>
  )
}

CommentTab.defaultProps = {
  handleCollapse: undefined
}
